/*
 * SCR-011 Upload Look controller — Phase A1. Reads and writes only through NcApp.gateway() (the mock in A1): createUpload /
 * deleteUpload per photo, searchProducts for the linked products, getProfile for the preview author, saveLookDraft and
 * publishLook for the two actions. No network, no persistence, no images of its own: the preview photo is the first photo
 * the user picked (the object URL nc-upload already made).
 *
 * Photos (INT-009): nc-upload validates type, size and count and fires add / retry / remove; each add uploads through
 * gateway.createUpload and reports NcUpload.setFileState(uploading | success | error); a failed upload keeps its slot with
 * "Reintentar" and a removed slot deletes its upload. Validation (TEST-009, INT-010) runs on blur (nc-input, limits) and on
 * the two actions, never per keystroke: the title (60) and description (300) limits are soft, so a 61st character can be typed
 * and is blocked with an inline error linked by aria-describedby; the first invalid control (photos, title, description in
 * reading order) takes focus. Publishing validates locally, then calls the gateway: pending / sent / error with no assumption
 * about moderation or timing ("enviado", never "publicado"); the form is never cleared, so a failure keeps the draft and the
 * same button retries. The draft only enforces the limits. Hosts get `nc-upload-look:published` {lookId, status} and
 * `nc-upload-look:draft-saved` {draftId} (navigation is Phase B).
 * Pure helpers are exported for node:test.
 */
(function () {
  var LIMITS = { photosMin: 1, photosMax: 5, title: 60, description: 300, tag: 24 };

  /**
   * v: { photoCount, unfinished, title, description } -> { photos?: 'min'|'max'|'pending', title?: 'required'|'max', description?: 'max' }
   * mode 'publish' also enforces requiredness (>= 1 finished photo, a title); 'draft' only the upper limits.
   */
  function validateLook(v, mode) {
    var errors = {};
    var title = String(v.title == null ? '' : v.title);
    var description = String(v.description == null ? '' : v.description);
    if (v.photoCount > LIMITS.photosMax) errors.photos = 'max';
    else if (mode === 'publish' && v.photoCount < LIMITS.photosMin) errors.photos = 'min';
    else if (mode === 'publish' && v.unfinished > 0) errors.photos = 'pending';
    if (title.length > LIMITS.title) errors.title = 'max';
    else if (mode === 'publish' && !title.trim()) errors.title = 'required';
    if (description.length > LIMITS.description) errors.description = 'max';
    return errors;
  }

  // first invalid control in reading order
  function firstInvalid(errors) {
    var order = ['photos', 'title', 'description'];
    for (var i = 0; i < order.length; i++) if (errors[order[i]]) return order[i];
    return null;
  }

  // "#Blanco jeans " -> "Blanco-jeans" (no leading #, no spaces, at most 24 characters); '' when nothing is left
  function normalizeTag(raw) {
    return String(raw == null ? '' : raw).trim().replace(/^#+/, '').trim().replace(/\s+/g, '-').slice(0, LIMITS.tag);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { validateLook: validateLook, firstInvalid: firstInvalid, normalizeTag: normalizeTag, LIMITS: LIMITS };
  if (typeof customElements === 'undefined' || customElements.get('nc-upload-look-screen')) return;

  function split(text) { return String(text || '').split('|').map(function (x) { return x.trim(); }); }
  function noop() {}

  class NcUploadLookScreen extends HTMLElement {
    connectedCallback() {
      this.gw = window.NcApp.gateway();
      this.photos = {}; // slot id -> { file, uploadId, status }
      this.tags = [];
      this.chosen = []; // linked products { id, title, brand }
      this.busy = false;
      this.productReq = 0;
      this.t = {
        draft: split(this.getAttribute('data-draft-states')),
        publish: split(this.getAttribute('data-publish-states')),
        preview: split(this.getAttribute('data-preview-texts')),
        products: split(this.getAttribute('data-product-texts')),
        messages: split(this.getAttribute('data-messages')),
        validation: split(this.getAttribute('data-validation-messages'))
      };
      this.tagsMax = parseInt(this.getAttribute('data-tags-max'), 10) || 5;
      this.productsMax = parseInt(this.getAttribute('data-products-max'), 10) || 4;

      this.upload = this.querySelector('nc-upload');
      this.titleEl = this.querySelector('#ul-title');
      this.descriptionEl = this.querySelector('#ul-description');
      this.titleField = this.titleEl && this.titleEl.closest('nc-input');
      this.descriptionField = this.descriptionEl && this.descriptionEl.closest('nc-input');
      // soft limits: the counter shows n/60 and the value can go over, so the error (not the browser) explains the limit
      [[this.titleField, this.titleEl, LIMITS.title], [this.descriptionField, this.descriptionEl, LIMITS.description]].forEach(function (p) {
        if (!p[0] || !p[1]) return;
        p[0].setAttribute('data-max-length', String(p[2]));
        p[1].removeAttribute('maxlength');
      });
      this.tagInput = this.querySelector('#ul-tags');
      this.tagList = this.querySelector('[data-ul-tag-list]');
      this.productInput = this.querySelector('#ul-products');
      this.productStatus = this.querySelector('[data-ul-product-status]');
      this.results = this.querySelector('[data-ul-results]');
      this.chosenList = this.querySelector('[data-ul-chosen]');
      this.previewHost = this.querySelector('[data-ul-preview]');
      this.draftBtn = this.querySelector('[data-ul-draft] .nc-button');
      this.publishBtn = this.querySelector('[data-ul-publish] .nc-button');
      this.visibility = this.querySelector('input[name="visibility"]');

      this.addEventListener('nc-upload:add', this.onAdd.bind(this));
      this.addEventListener('nc-upload:retry', this.onRetry.bind(this));
      this.addEventListener('nc-upload:remove', this.onRemove.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('nc-select:change', this.touched.bind(this));
      this.addEventListener('nc-option:change', this.onChoice.bind(this));
      this.addEventListener('nc-chip:remove', this.onChipRemove.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('click', this.onClick.bind(this));

      this.renderPreview();
      var self = this;
      this.gw.getProfile().then(function (p) {
        var handle = self.querySelector('.nc-post-preview__handle');
        if (handle && p && p.handle) handle.textContent = p.handle;
      }, noop);
      this.gw.getAuthState().then(function (a) {
        var notice = self.querySelector('[data-ul-notice]');
        if (notice && a && a.status !== 'authenticated') notice.hidden = false;
      }, noop);
    }

    // ---- photos (INT-009) ----
    onAdd(event) {
      var id = event.detail.id;
      this.photos[id] = { file: event.detail.file, uploadId: null, status: 'uploading' };
      this.startUpload(id);
      this.touched();
      this.renderPreview();
    }

    onRetry(event) {
      if (this.photos[event.detail.id]) this.startUpload(event.detail.id);
    }

    startUpload(id) {
      var self = this;
      var photo = this.photos[id];
      photo.status = 'uploading';
      window.NcUpload.setFileState(this.upload, id, 'uploading', 0);
      this.gw.createUpload({ file: photo.file }).then(function (up) {
        if (self.photos[id] !== photo) { self.gw.deleteUpload(up.id).then(noop, noop); return; } // removed while uploading
        photo.uploadId = up.id;
        photo.status = 'success';
        window.NcUpload.setFileState(self.upload, id, 'success', 100);
      }, function () {
        if (self.photos[id] !== photo) return;
        photo.status = 'error';
        window.NcUpload.setFileState(self.upload, id, 'error', 0);
      });
    }

    onRemove(event) {
      var photo = this.photos[event.detail.id];
      delete this.photos[event.detail.id];
      if (photo && photo.uploadId) this.gw.deleteUpload(photo.uploadId).then(noop, noop);
      this.touched();
      this.renderPreview();
    }

    get slots() { return this.upload ? this.upload.files : []; }

    // upload ids of the finished photos, in on-screen order
    get uploadIds() {
      var self = this;
      return this.slots.map(function (s) { return self.photos[s.getAttribute('data-id')]; })
        .filter(function (p) { return p && p.status === 'success'; })
        .map(function (p) { return p.uploadId; });
    }

    // ---- live preview (step 3) ----
    onInput(event) {
      var id = event.target && event.target.id;
      if (id === 'ul-title' || id === 'ul-description') { this.touched(); this.renderPreview(); }
      else if (id === 'ul-products') this.queueSearch();
    }

    // occasion / style / season (nc-choice-group): the preview shows what was picked
    onChoice() {
      this.touched();
      this.renderPreview();
    }

    choiceLabel(name) {
      var i = this.querySelector('input[name="' + name + '"]:checked');
      return i ? (i.getAttribute('data-label') || i.value) : '';
    }

    // Figma 15:693 post: occasion and season chips first, then the rest folded into "+N" (two chips visible).
    // The order and the fold are INFERRED from the frame; the full list stays in the accessible name of "+N".
    previewChips() {
      var all = [this.choiceLabel('occasion'), this.choiceLabel('season'), this.choiceLabel('style')]
        .filter(Boolean)
        .concat(this.tags.map(function (t) { return '#' + t; }));
      if (all.length <= 2) return all.map(function (t) { return { text: t }; });
      var rest = all.slice(2);
      return [{ text: all[0] }, { text: all[1] }, { text: '+' + rest.length, label: rest.join(', ') }];
    }

    renderPreview() {
      var host = this.previewHost;
      if (!host) return;
      var title = (this.titleEl && this.titleEl.value || '').trim();
      var description = (this.descriptionEl && this.descriptionEl.value || '').trim();
      var h = host.querySelector('.nc-post-preview__title');
      h.textContent = title || host.getAttribute('data-empty-title');
      h.classList.toggle('nc-post-preview__title--empty', !title);
      var body = host.querySelector('.nc-post-preview__body');
      var text = host.querySelector('.nc-post-preview__text');
      if (!text) {
        text = document.createElement('p');
        text.className = 'nc-post-preview__text';
        h.insertAdjacentElement('afterend', text);
      }
      text.textContent = description || host.getAttribute('data-empty-body');
      this.syncList(body, 'nc-post-preview__tags', 'Etiquetas', this.previewChips(), text);
      var tagsEl = body.querySelector('.nc-post-preview__tags');
      this.syncList(body, 'nc-post-preview__products', 'Prendas del look', this.chosen.map(function (p) { return { text: p.title }; }), tagsEl || text);
      // first photo = cover; the slot's img already holds the user's object URL
      var media = host.querySelector('.nc-post-preview__media');
      var first = this.slots[0];
      var src = first && first.querySelector('img') && first.querySelector('img').getAttribute('src');
      var img = media.querySelector('img');
      if (src) {
        if (!img) { img = document.createElement('img'); img.setAttribute('width', '393'); img.setAttribute('height', '338'); img.setAttribute('alt', ''); media.appendChild(img); }
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
      } else if (img) img.remove();
    }

    // keeps <ul class=cls> in sync with `items` (created after `after`, removed when empty)
    syncList(parent, cls, label, items, after) {
      var ul = parent.querySelector('.' + cls);
      if (!items.length) { if (ul) ul.remove(); return; }
      if (!ul) {
        ul = document.createElement('ul');
        ul.className = cls;
        ul.setAttribute('aria-label', label);
        after.insertAdjacentElement('afterend', ul);
      }
      ul.textContent = '';
      items.forEach(function (t) {
        var li = document.createElement('li');
        li.textContent = t.text;
        if (t.label) li.setAttribute('aria-label', t.label);
        ul.appendChild(li);
      });
    }

    setPreviewStatus(status, label) {
      var article = this.previewHost.querySelector('.nc-post-preview');
      var p = article.querySelector('.nc-post-preview__status');
      article.setAttribute('data-status', status);
      if (status === 'pending') article.setAttribute('aria-busy', 'true'); else article.removeAttribute('aria-busy');
      p.textContent = '';
      if (status === 'error') p.insertAdjacentHTML('afterbegin', '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>');
      if (status === 'pending') p.insertAdjacentHTML('afterbegin', '<span class="nc-post-preview__spinner" aria-hidden="true"></span>');
      p.appendChild(document.createTextNode(label));
    }

    // any edit after an outcome puts the buttons and the preview status back to their default
    touched() {
      var wasSent = this.sent;
      this.sent = false;
      [this.draftBtn, this.publishBtn].forEach(function (b) {
        var st = b && b.getAttribute('data-state');
        if (st === 'success' || st === 'error') b.setAttribute('data-state', 'default');
      });
      var article = this.previewHost && this.previewHost.querySelector('.nc-post-preview');
      if (article && !this.busy && (wasSent || article.getAttribute('data-status') !== 'preview')) this.setPreviewStatus('preview', this.t.preview[2]);
    }

    // ---- tags (optional, CMP-006 applied chips) ----
    onKeydown(event) {
      if (event.target === this.tagInput && (event.key === 'Enter' || event.key === ',')) {
        event.preventDefault();
        this.commitTag();
      } else if (event.target === this.productInput && event.key === 'Enter') {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        this.search();
      }
    }

    onFocusOut(event) {
      if (event.target === this.tagInput) this.commitTag();
    }

    chip(templateName, label, removeLabel, value) {
      var chip = this.querySelector('template[data-ul-tpl="' + templateName + '"]').content.querySelector('nc-chip').cloneNode(true);
      chip.querySelector('.nc-chip__main').textContent = label;
      chip.querySelector('.nc-chip__remove').setAttribute('aria-label', removeLabel + ': ' + label);
      chip.setAttribute('data-value', value);
      return chip;
    }

    commitTag() {
      var tag = normalizeTag(this.tagInput.value);
      if (!tag) { this.tagInput.value = ''; return; }
      var exists = this.tags.some(function (t) { return t.toLowerCase() === tag.toLowerCase(); });
      if (!exists && this.tags.length >= this.tagsMax) {
        this.tagField().setError((this.t.messages[6] || '').replace('{n}', this.tagsMax));
        return;
      }
      this.tagField().clearError();
      this.tagInput.value = '';
      if (exists) return;
      this.tags.push(tag);
      this.tagList.appendChild(this.chip('tag', '#' + tag, this.t.messages[7] || 'Quitar etiqueta', tag));
      this.tagList.hidden = false;
      this.touched();
      this.renderPreview();
    }

    tagField() { return this.tagInput.closest('nc-input'); }

    onChipRemove(event) {
      var chip = event.target.closest('nc-chip');
      if (!chip) return;
      var list = chip.parentElement;
      var value = chip.getAttribute('data-value');
      event.preventDefault(); // this host removes the chip so focus never lands on a hidden list
      var next = chip.nextElementSibling || chip.previousElementSibling;
      chip.remove();
      if (list === this.tagList) this.tags = this.tags.filter(function (t) { return t !== value; });
      else if (list === this.chosenList) { this.chosen = this.chosen.filter(function (p) { return p.id !== value; }); this.search(true); }
      list.hidden = !list.querySelector('nc-chip');
      var target = next ? next.querySelector('.nc-chip__main') : (list === this.tagList ? this.tagInput : this.productInput);
      target.focus();
      this.touched();
      this.renderPreview();
    }

    // ---- linked products (gateway.searchProducts) ----
    queueSearch() {
      clearTimeout(this.searchTimer);
      var self = this;
      this.searchTimer = setTimeout(function () { self.search(); }, 250);
    }

    setProductStatus(text) {
      this.productStatus.textContent = text || '';
      this.productStatus.hidden = !text;
    }

    search(keep) {
      var self = this;
      var query = this.productInput.value.trim();
      var seq = ++this.productReq;
      if (!query) { this.results.hidden = true; this.results.textContent = ''; this.setProductStatus(''); return; }
      if (!keep) this.setProductStatus(this.t.products[1] || '');
      this.gw.searchProducts({ query: query, page: 1 }).then(function (page) {
        if (seq !== self.productReq) return;
        var ids = self.chosen.map(function (p) { return p.id; });
        var items = page.items.filter(function (p) { return ids.indexOf(p.id) < 0; });
        self.results.textContent = '';
        items.forEach(function (p) {
          var li = document.createElement('li');
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'nc-upload-look__result';
          btn.setAttribute('data-id', p.id);
          btn.setAttribute('data-title', p.title);
          btn.setAttribute('data-brand', p.brand || '');
          btn.setAttribute('aria-label', (self.t.products[0] || 'Añadir') + ': ' + p.title);
          var name = document.createElement('span');
          name.className = 'nc-upload-look__result-name';
          name.textContent = p.title;
          var meta = document.createElement('span');
          meta.className = 'nc-upload-look__result-meta';
          meta.textContent = [p.brand, p.price].filter(Boolean).join(' · ');
          var add = document.createElement('span');
          add.className = 'nc-upload-look__result-add';
          add.setAttribute('aria-hidden', 'true');
          add.textContent = self.t.products[0] || 'Añadir';
          btn.appendChild(name); btn.appendChild(meta); btn.appendChild(add);
          li.appendChild(btn);
          self.results.appendChild(li);
        });
        self.results.hidden = !items.length;
        self.setProductStatus(items.length ? '' : (self.t.products[2] || ''));
      }, function () {
        if (seq !== self.productReq) return;
        self.results.hidden = true;
        self.setProductStatus(self.t.products[3] || '');
      });
    }

    onClick(event) {
      var result = event.target.closest('.nc-upload-look__result');
      if (result) return this.addProduct(result);
      if (this.draftBtn && this.draftBtn.contains(event.target)) return this.save('draft');
      if (this.publishBtn && this.publishBtn.contains(event.target)) return this.save('publish');
    }

    addProduct(btn) {
      if (this.chosen.length >= this.productsMax) {
        this.setProductStatus((this.t.products[4] || '').replace('{n}', this.productsMax));
        return;
      }
      var product = { id: btn.getAttribute('data-id'), title: btn.getAttribute('data-title'), brand: btn.getAttribute('data-brand') };
      var li = btn.closest('li');
      var next = li.nextElementSibling || li.previousElementSibling;
      this.chosen.push(product);
      this.chosenList.appendChild(this.chip('product', product.title, this.t.products[5] || 'Quitar prenda', product.id));
      this.chosenList.hidden = false;
      li.remove();
      this.results.hidden = !this.results.children.length;
      if (next && next.isConnected) next.querySelector('button').focus(); else this.productInput.focus();
      this.touched();
      this.renderPreview();
    }

    // ---- draft / publish (INT-010) ----
    values() {
      var unfinished = this.slots.filter(function (s) { return s.getAttribute('data-state') !== 'success'; }).length;
      var sel = function (name) { var i = this.querySelector('input[name="' + name + '"]:checked, input[type="hidden"][name="' + name + '"]'); return i ? i.value : ''; }.bind(this);
      return {
        photoCount: this.slots.length,
        unfinished: unfinished,
        title: this.titleEl ? this.titleEl.value : '',
        description: this.descriptionEl ? this.descriptionEl.value : '',
        payload: {
          title: (this.titleEl ? this.titleEl.value : '').trim(),
          description: (this.descriptionEl ? this.descriptionEl.value : '').trim(),
          occasion: sel('occasion'),
          style: sel('style'),
          season: sel('season'),
          visibility: sel('visibility'),
          tags: this.tags.slice(),
          productIds: this.chosen.map(function (p) { return p.id; }),
          uploadIds: this.uploadIds
        }
      };
    }

    // shows each error inline (linked by aria-describedby) and focuses the first invalid control
    showErrors(errors) {
      var msg = this.t.validation;
      var first = firstInvalid(errors);
      if (errors.photos) {
        if (errors.photos === 'max') {
          this.upload.showErrors([this.upload.msg('max')]);
          var last = this.slots[this.slots.length - 1];
          if (first === 'photos' && last) last.querySelector('.nc-upload__remove').focus();
        } else window.NcUpload.validate(this.upload); // min / pending: its own message and focus (add control, retry or remove)
      } else if (this.upload) this.upload.showErrors([]);
      if (errors.title) this.titleField.setError(errors.title === 'required' ? msg[0] : msg[1]);
      else this.titleField.validate();
      if (errors.description) this.descriptionField.setError(msg[2]); else this.descriptionField.validate();
      if (first === 'title') this.titleEl.focus();
      else if (first === 'description') this.descriptionEl.focus();
    }

    setBusy(on, which) {
      this.busy = on;
      [this.draftBtn, this.publishBtn].forEach(function (b) {
        if (!b || (on && b === (which === 'draft' ? this.draftBtn : this.publishBtn))) return;
        if (on) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
      }, this);
    }

    save(mode) {
      if (this.busy || (mode === 'publish' && this.sent)) return;
      var self = this;
      var v = this.values();
      var errors = validateLook(v, mode);
      if (Object.keys(errors).length) { this.showErrors(errors); return; }
      this.titleField.clearError();
      this.descriptionField.clearError();
      var btn = mode === 'draft' ? this.draftBtn : this.publishBtn;
      var publish = mode === 'publish';
      this.setBusy(true, mode);
      btn.setAttribute('data-state', 'loading');
      if (publish) this.setPreviewStatus('pending', this.t.preview[3]);
      var call = publish ? this.gw.publishLook(v.payload) : this.gw.saveLookDraft(v.payload);
      call.then(function (res) {
        self.setBusy(false);
        btn.setAttribute('data-state', 'success');
        if (publish) {
          self.sent = true;
          self.setPreviewStatus('preview', self.t.preview[5]);
          window.NcToast.show({ tone: 'success', message: self.t.messages[2], detail: self.t.messages[3] });
          self.dispatchEvent(new CustomEvent('nc-upload-look:published', { bubbles: true, detail: { lookId: res.lookId, status: res.status } }));
        } else {
          window.NcToast.show({ tone: 'success', message: self.t.messages[0], detail: self.t.messages[1] });
          self.dispatchEvent(new CustomEvent('nc-upload-look:draft-saved', { bubbles: true, detail: { draftId: res.draftId } }));
        }
      }, function (err) {
        self.setBusy(false);
        var fields = err && err.kind === 'validation' && err.fieldErrors;
        var mapped = {};
        if (fields) {
          if (fields.title) mapped.title = fields.title;
          if (fields.description) mapped.description = fields.description;
          if (fields.uploadIds) mapped.photos = fields.uploadIds;
        }
        if (Object.keys(mapped).length) {
          // server-side field errors: shown inline, nothing is cleared
          btn.setAttribute('data-state', 'default');
          if (publish) self.setPreviewStatus('preview', self.t.preview[2]);
          if (mapped.photos) { self.upload.showErrors([mapped.photos]); }
          if (mapped.title) self.titleField.setError(mapped.title);
          if (mapped.description) self.descriptionField.setError(mapped.description);
          var key = firstInvalid(mapped);
          if (key === 'title') self.titleEl.focus(); else if (key === 'description') self.descriptionEl.focus(); else self.upload.input.focus();
          return;
        }
        btn.setAttribute('data-state', 'error');
        if (publish) self.setPreviewStatus('error', self.t.preview[4]);
        window.NcToast.show({ tone: 'error', message: publish ? self.t.messages[4] : self.t.messages[5], detail: err && err.message });
      });
    }
  }

  customElements.define('nc-upload-look-screen', NcUploadLookScreen);
})();
